import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Descriptions, Button, Space, Tag, Table, Statistic, Spin, message, Modal, Tooltip, Typography } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined, DownloadOutlined, EditOutlined, InfoCircleOutlined, DeleteOutlined, FileTextOutlined } from '@ant-design/icons';
import axios from 'axios';
import logger from '../../services/logger';
import { safeFormatDate } from '../../utils/timezone';
import { useAuth } from '../../contexts/AuthContext';

const InvoiceDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCustomer = user?.role?.name === 'customer';
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  
  // URL'den referrer bilgisini al
  const searchParams = new URLSearchParams(window.location.search);
  const referrer = searchParams.get('from');

  useEffect(() => {
    fetchInvoiceDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvoiceDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/v1/billing/invoices/${id}`);
      setInvoice(response.data);
    } catch (error) {
      logger.error('Fatura detayı yüklenemedi:', error);
      if (error.response?.status === 403) {
        message.error('Bu işlem için yetkiniz bulunmuyor.');
      } else if (error.response?.status === 401) {
        message.error('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
        navigate('/login');
      } else {
        message.error('Fatura detayları yüklenirken bir hata oluştu.');
      }
    } finally {
      setLoading(false);
    }
  };

    const handleAddPayment = async () => {
    try {
      Modal.confirm({
        title: `Ödeme Ekle - Fatura #${invoice.invoice_number}`,
        content: (
          <div>
            <p><strong>Fatura Tutarı:</strong> ₺{parseFloat(invoice.total_amount || 0).toFixed(2)}</p>
            <p>Bu fatura için ödeme eklemek istediğinizden emin misiniz?</p>
            <p>Ödeme eklendikten sonra fatura durumu "Ödendi" olarak değişecektir.</p>
          </div>
        ),
        onOk: async () => {
          try {
            // Yeni endpoint kullan
            await axios.post(`/api/v1/billing/invoices/${id}/add-payment`);

            message.success('Ödeme başarıyla eklendi.');

            // Fatura detaylarını yenile
            fetchInvoiceDetail();
          } catch (error) {
            logger.error('Ödeme eklenemedi:', error);
            message.error('Ödeme eklenirken bir hata oluştu.');
          }
        },
        width: 500,
      });
    } catch (error) {
      logger.error('Ödeme ekleme hatası:', error);
      message.error('Ödeme ekleme işlemi başlatılamadı.');
    }
  };

  const handlePrint = () => {
    // Fatura yazdırma için yeni pencere aç
    const printWindow = window.open('', '_blank');
    const invoiceContent = generateInvoiceHTML();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Fatura #${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .invoice-info { margin-bottom: 20px; }
            .customer-info { margin-bottom: 20px; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items-table th { background-color: #f2f2f2; }
            .totals { text-align: right; margin-top: 20px; }
            .total-row { font-weight: bold; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${invoiceContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownloadPDF = () => {
    // PDF indirme için yeni pencere aç ve yazdır
    const printWindow = window.open('', '_blank');
    const invoiceContent = generateInvoiceHTML();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Fatura #${invoice.invoice_number}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .header { text-align: center; margin-bottom: 30px; }
            .invoice-info { margin-bottom: 20px; }
            .customer-info { margin-bottom: 20px; }
            .items-table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            .items-table th { background-color: #f2f2f2; }
            .totals { text-align: right; margin-top: 20px; }
            .total-row { font-weight: bold; }
            @media print { body { margin: 0; } }
            @page { size: A4; margin: 1cm; }
          </style>
        </head>
        <body>
          ${invoiceContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleEdit = () => {
    // Fatura düzenleme sayfasına yönlendir
    navigate(`/billing/edit/${invoice.id}`);
  };

  const generateInvoiceHTML = () => {
    const formatDate = (date) => {
      return date ? safeFormatDate(date) : 'Belirtilmemiş';
    };

    const formatCurrency = (amount) => {
      return `₺${parseFloat(amount || 0).toFixed(2)}`;
    };

    return `
      <div class="header">
        <h1>FATURA</h1>
        <h2>#${invoice.invoice_number}</h2>
      </div>
      
      <div class="invoice-info">
        <p><strong>Fatura Tarihi:</strong> ${formatDate(invoice.issue_date)}</p>
        <p><strong>Dönem:</strong> ${invoice.billing_period}</p>
        <p><strong>Durum:</strong> ${getStatusText(invoice.status)}</p>
      </div>
      
      <div class="customer-info">
        <h3>Müşteri Bilgileri</h3>
        <p><strong>Ad:</strong> ${invoice.customer?.name || 'Bilinmiyor'}</p>
        <p><strong>E-posta:</strong> ${invoice.customer?.email || 'Belirtilmemiş'}</p>
        <p><strong>Telefon:</strong> ${invoice.customer?.phone || 'Belirtilmemiş'}</p>
      </div>
      
      <table class="items-table">
        <thead>
          <tr>
            <th>Cihaz</th>
            <th>Açıklama</th>
            <th>Seans Zamanı</th>
            <th>Tip</th>
            <th>Detay</th>
            <th>KDV</th>
            <th>Toplam</th>
          </tr>
        </thead>
        <tbody>
          ${(invoice.invoice_items || []).map(item => {
            let periodText = '-';
            if (item.period_start && item.period_end) {
              const start = new Date(item.period_start);
              const end = new Date(item.period_end);
              const duration = Math.round((end - start) / 60000);
              periodText = start.toLocaleDateString('tr-TR') + ' ' + 
                          start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) + ' - ' + 
                          end.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) + 
                          ' (' + duration + ' dk)';
            }
            return `
            <tr>
              <td>#${item.device_id || '-'}</td>
              <td>${item.description || 'Açıklama yok'}</td>
              <td style="font-size: 10px;">${periodText}</td>
              <td style="text-align: center;">${item.billing_type === 'fixed_monthly' ? 'Sabit Aylık' : item.billing_type === 'per_shot' ? 'Atış Bazlı' : '-'}</td>
              <td style="text-align: right;">${item.billing_type === 'fixed_monthly' ? (item.monthly_rate ? formatCurrency(item.monthly_rate) + '/ay' : '-') : (item.rental_shots_count ? item.rental_shots_count + ' atış x ' + formatCurrency(item.rental_unit_price) : '-')}</td>
              <td style="text-align: center;">%${parseFloat(item.vat_rate || 0).toFixed(0)}</td>
              <td style="text-align: right;">${formatCurrency(item.total_price)}</td>
            </tr>
          `;
          }).join('')}
        </tbody>
      </table>
      
      <div class="totals">
        <p>Ara Toplam: ${formatCurrency(invoice.subtotal)}</p>
        <p>KDV: ${formatCurrency(invoice.vat_amount)}</p>
        <p class="total-row">Genel Toplam: ${formatCurrency(invoice.total_amount)}</p>
      </div>
      
      ${invoice.description ? `<div style="margin-top: 30px;"><strong>Açıklama:</strong> ${invoice.description}</div>` : ''}
    `;
  };

  const handleDeleteInvoice = () => {
    Modal.confirm({
      title: 'Fatura Sil',
      content: (
        <div>
          <p><strong>Fatura #{invoice.invoice_number}</strong> silinecek.</p>
          <p>Fatura kalemleri de silinecek ve ilgili kiralamalar tekrar faturalanabilir olacak.</p>
          <p>Bu işlem geri alınamaz. Devam etmek istediğinizden emin misiniz?</p>
        </div>
      ),
      onOk: async () => {
        try {
          await axios.delete(`/api/v1/billing/invoices/${id}`);
          message.success('Fatura başarıyla silindi.');
          navigate('/billing');
        } catch (error) {
          logger.error('Fatura silinemedi:', error);
          message.error(error.response?.data?.detail || 'Fatura silinirken bir hata oluştu.');
        }
      },
      okText: 'Sil',
      cancelText: 'Vazgeç',
      okType: 'danger',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'orange',
      paid: 'green',
      overdue: 'red',
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'Bekliyor',
      paid: 'Ödendi',
      overdue: 'Vadesi Geçti',
    };
    return texts[status] || status;
  };



  const invoiceItemsColumns = [
    {
      title: 'Cihaz',
      dataIndex: 'description',
      key: 'device',
      width: 150,
      render: (desc, record) => {
        // Description format: "Kiralama: CihazAdı - Dönem"
        const match = desc?.match(/Kiralama:\s*(.+?)\s*-/);
        return match ? match[1] : `#${record.device_id}`;
      },
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Seans Zamanı',
      key: 'period',
      width: 180,
      render: (_, record) => {
        if (record.period_start && record.period_end) {
          const start = new Date(record.period_start);
          const end = new Date(record.period_end);
          const duration = Math.round((end - start) / 60000); // minutes
          return (
            <div>
              <div style={{ fontSize: '12px' }}>
                {start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })} - 
                {end.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
              </div>
              <div style={{ fontSize: '11px', color: '#888' }}>
                {start.toLocaleDateString('tr-TR')} ({duration} dk)
              </div>
            </div>
          );
        }
        return '-';
      },
    },
    {
      title: 'Faturalama Tipi',
      dataIndex: 'billing_type',
      key: 'billing_type',
      width: 120,
      align: 'center',
      render: (value) => <Tag color={value === 'fixed_monthly' ? 'blue' : value === 'per_shot' ? 'purple' : 'default'}>{value === 'fixed_monthly' ? 'Sabit Aylık' : value === 'per_shot' ? 'Atış Bazlı' : value || '-'}</Tag>,
    },
    {
      title: 'Detay',
      key: 'billing_detail',
      width: 150,
      align: 'right',
      render: (_, record) => {
        if (record.billing_type === 'fixed_monthly') {
          const rate = record.monthly_rate ? `₺${parseFloat(record.monthly_rate).toFixed(2)}/ay` : '';
          const days = record.rental_days ? `${record.rental_days} gün` : '';
          return <div>{rate}{days ? <div style={{ fontSize: '11px', color: '#888' }}>{days}</div> : null}</div>;
        }
        if (record.billing_type === 'per_shot') {
          const price = record.rental_unit_price ? `₺${parseFloat(record.rental_unit_price).toFixed(4)}/atış` : '';
          const shots = record.rental_shots_count ? `${record.rental_shots_count} atış` : '';
          return <div>{price}{shots ? <div style={{ fontSize: '11px', color: '#888' }}>{shots}</div> : null}</div>;
        }
        return '-';
      },
    },
    {
      title: 'KDV',
      dataIndex: 'vat_rate',
      key: 'vat_rate',
      width: 80,
      align: 'center',
      render: (value) => `%${parseFloat(value || 0).toFixed(0)}`,
    },
    {
      title: 'Toplam',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 110,
      align: 'right',
      render: (value) => `₺${parseFloat(value || 0).toFixed(2)}`,
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p>Fatura detayları yükleniyor...</p>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <p>Fatura bulunamadı.</p>
        <Button type="primary" onClick={() => navigate('/billing')}>
          Faturalara Dön
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
                  <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => {
              if (referrer === 'customer') {
                // Müşteri detay sayfasından geldiyse, müşteri detay sayfasına dön ve faturalar tabını seç
                navigate(`/customers/${invoice?.customer_id}?tab=invoices`);
              } else {
                // Diğer durumlarda faturalar sayfasına dön
                navigate('/billing');
              }
            }}
            style={{ marginBottom: 16 }}
          >
            {referrer === 'customer' ? 'Müşteriye Dön' : 'Faturalara Dön'}
          </Button>
        
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Typography.Title level={2} style={{ margin: 0 }}>
              <FileTextOutlined /> Fatura #{invoice.invoice_number}
            </Typography.Title>
          </Col>
          <Col>
            <Space>
              <Button icon={<PrinterOutlined />} onClick={handlePrint}>
                Yazdır
              </Button>
              <Button icon={<DownloadOutlined />} onClick={handleDownloadPDF}>
                PDF İndir
              </Button>
              {!isCustomer && invoice.status !== 'paid' && (
                <Button icon={<EditOutlined />} onClick={handleEdit}>
                  Düzenle
                </Button>
              )}
            </Space>
          </Col>
        </Row>
      </div>

      {/* Özet Kartları */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Ara Toplam"
              value={parseFloat(invoice.subtotal || 0)}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="KDV"
              value={parseFloat(invoice.vat_amount || 0)}
              suffix="₺"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Toplam Tutar"
              value={parseFloat(invoice.total_amount || 0)}
              suffix="₺"
              precision={2}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Durum"
              value={getStatusText(invoice.status)}
              valueStyle={{ color: getStatusColor(invoice.status) === 'green' ? '#3f8600' : '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Fatura Bilgileri */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Fatura Bilgileri">
            <Descriptions column={1}>
              <Descriptions.Item label="Fatura No">
                {invoice.invoice_number}
              </Descriptions.Item>
              <Descriptions.Item label="Müşteri">
                {invoice.customer?.name || 'Bilinmiyor'}
              </Descriptions.Item>
              <Descriptions.Item label="Dönem">
                {invoice.billing_period}
                <Tooltip title="Faturalandırma dönemi">
                  <InfoCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
                </Tooltip>
              </Descriptions.Item>
              <Descriptions.Item label="Fatura Tarihi">
                {invoice.issue_date ? safeFormatDate(invoice.issue_date) : 'Belirtilmemiş'}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                <Tag color={getStatusColor(invoice.status)}>
                  {getStatusText(invoice.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Açıklama">
                {invoice.description || 'Açıklama yok'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        
        <Col span={12}>
          {!isCustomer && (
          <Card title="İşlemler">
            <Space direction="vertical" style={{ width: '100%' }}>
              {(invoice.status === 'pending' || invoice.status === 'overdue') && (
                <Button
                  type="primary"
                  size="large"
                  block
                  onClick={handleAddPayment}
                >
                  Ödeme Ekle
                </Button>
              )}

              {invoice.status !== 'paid' && (
                <Button size="large" block onClick={handleEdit}>
                  Fatura Düzenle
                </Button>
              )}

              <Button size="large" block danger icon={<DeleteOutlined />} onClick={handleDeleteInvoice}>
                Fatura Sil
              </Button>
            </Space>
          </Card>
          )}
        </Col>
      </Row>

      {/* Fatura Kalemleri */}
      <Card title="Fatura Kalemleri" style={{ marginTop: 24 }}>
        <Table
          columns={invoiceItemsColumns}
          dataSource={invoice.invoice_items || []}
          rowKey="id"
          pagination={false}
          scroll={{ x: 1200 }}
          locale={{ emptyText: 'Fatura kalemi bulunamadı' }}
          summary={(pageData) => {
            const subtotal = pageData.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                  <Table.Summary.Cell index={0} colSpan={5} align="right">
                    TOPLAM:
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} align="right">
                    ₺{subtotal.toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Card>
    </div>
  );
};

export default InvoiceDetail;
